/**
 * themes.js
 * Theme management and dynamic CSS loading
 */

import { state } from './state.js?v=302';
import { elements } from './dom.js?v=302';
import { icons } from './icons.js?v=302';
import { showToast } from './ui.js?v=302';

/**
 * Apply the currently selected theme and mode
 * Loads theme CSS dynamically and updates Prism syntax highlighting
 */
export function applyTheme() {
  document.body.classList.add('theme-transitioning');

  // Remove all theme+mode classes first
  const themeModeClasses = [
    "theme-noir-light",
    "theme-noir-dark",
    "theme-ocean-light",
    "theme-ocean-dark",
    "theme-liquid-light",
    "theme-liquid-dark",
    "theme-desert-light",
    "theme-desert-dark",
    "theme-garden-light",
    "theme-garden-dark",
    "theme-jade-light",
    "theme-jade-dark",
    "theme-zen-light",
    "theme-zen-dark",
    "theme-glass-light",
    "theme-glass-dark",
  ];

  for (const cls of themeModeClasses) {
    document.body.classList.remove(cls);
  }

  // Apply new theme+mode class
  document.body.classList.add(
    `theme-${state.selectedTheme}-${state.theme}`,
  );

  // Load theme CSS dynamically
  loadTheme(state.selectedTheme, state.theme);

  updateThemeIcon();

  // Update Prism theme for code blocks
  const prismLink = document.getElementById("prism-theme-link");
  if (prismLink) {
    const prismTheme =
      state.theme === "dark"
        ? "https://cdn.jsdelivr.net/npm/prismjs@1.29.0/themes/prism-tomorrow.min.css"
        : "https://cdn.jsdelivr.net/npm/prismjs@1.29.0/themes/prism.min.css";
    prismLink.href = prismTheme;
  }

  setTimeout(() => document.body.classList.remove('theme-transitioning'), 500);
}

/**
 * Dynamically load theme CSS file
 * Removes previous theme link and adds new one
 * @param {string} themeName - Theme identifier (e.g., 'noir', 'ocean')
 * @param {string} mode - Theme mode ('light' or 'dark')
 */
function loadTheme(themeName, mode) {
  const themeId = `${themeName}-${mode}`;

  // Remove existing theme link if it exists
  const existingLink = document.getElementById('dynamic-theme-link');
  if (existingLink) {
    existingLink.remove();
  }

  // Create and append new theme link
  const link = document.createElement('link');
  link.id = 'dynamic-theme-link';
  link.rel = 'stylesheet';
  link.href = `css/themes/${themeId}.css`;

  // Append to head
  document.head.appendChild(link);
}

/**
 * Render the themes selection tab in settings
 * Shows all available themes with color swatches and descriptions
 */
export function renderThemesTab() {
  const themesContainer = document.getElementById("themes-tab-content");
  if (!themesContainer) return;

  const themes = [
    {
      id: "noir",
      name: "Executive Noir",
      description: "Sophisticated, professional, authoritative",
      colors: ["oklch(0.68 0.12 75)", "oklch(0.22 0.005 60)", "oklch(0.98 0.002 60)"],
    },
    {
      id: "ocean",
      name: "Ocean Depths",
      description: "Serene, educational, oceanic",
      colors: ["oklch(0.45 0.15 220)", "oklch(0.55 0.18 200)", "oklch(0.55 0.14 200)"],
    },
    {
      id: "liquid",
      name: "Liquid Glass",
      description: "Warm, translucent, rose-gold glass",
      colors: ["oklch(0.65 0.18 25)", "oklch(0.68 0.16 20)", "oklch(0.72 0.14 30)"],
    },
    {
      id: "desert",
      name: "Desert Sunset",
      description: "Warm, bold, energetic",
      colors: ["oklch(0.70 0.16 35)", "oklch(0.72 0.14 35)", "oklch(0.80 0.12 45)"],
    },
    {
      id: "garden",
      name: "Midnight Garden",
      description: "Elegant, creative, refined",
      colors: ["oklch(0.48 0.16 320)", "oklch(0.58 0.12 280)", "oklch(0.50 0.14 340)"],
    },
    {
      id: "jade",
      name: "Jade",
      description: "Rich, vibrant, gemstone-inspired",
      colors: ["oklch(0.65 0.18 160)", "oklch(0.60 0.16 155)", "oklch(0.70 0.14 165)"],
    },
    {
      id: "zen",
      name: "Zen Garden",
      description: "Calm, natural, wabi-sabi beauty",
      colors: ["oklch(0.42 0.05 150)", "oklch(0.58 0.08 55)", "oklch(0.50 0.10 30)"],
    },
    {
      id: "glass",
      name: "macOS Glass",
      description: "Translucent, modern, ethereal",
      colors: ["oklch(0.50 0.15 250)", "oklch(0.65 0.15 280)", "oklch(0.60 0.18 260)"],
    },
  ];

  const themeCards = themes
    .map(
      (theme) => `
        <label class="theme-card" style="
          border: 2px solid var(--border-subtle);
          border-radius: 12px;
          padding: var(--space-4);
          cursor: pointer;
          transition: all var(--duration-base);
          position: relative;
        ">
          <input type="radio" name="theme-selection" value="${theme.id}" ${state.selectedTheme === theme.id ? "checked" : ""
        } style="position: absolute; opacity: 0; width: 0; height: 0;">

          <div style="display: flex; gap: 6px; margin-bottom: var(--space-3);">
            ${theme.colors
          .map(
            (color) =>
              `<div class="swatch" style="width: 24px; height: 24px; border-radius: 50%; background: ${color};"></div>`,
          )
          .join("")}
          </div>

          <div style="margin-top: var(--space-3);">
            <h3 style="font-family: var(--font-display); font-size: var(--text-xl); color: var(--text-primary); margin: 0 0 0 0;">
              ${theme.name}
            </h3>
            <p style="font-size: var(--text-sm); color: var(--text-secondary); margin: 0; line-height: 1.5;">
              ${theme.description}
            </p>
          </div>

          <div class="active-indicator" style="position: absolute; top: 8px; right: 8px; width: 10px; height: 10px; background: var(--accent-primary); border-radius: 50%; opacity: 0; transform: scale(0); transition: all var(--ease-spring);"></div>
        </label>
      `,
    )
    .join("");

  themesContainer.innerHTML = `
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--space-4); margin-bottom: var(--space-4);">
        ${themeCards}
      </div>
    `;
}

/**
 * Save selected theme from radio buttons and apply it
 */
export function saveThemeSelection() {
  const selectedRadio = document.querySelector(
    'input[name="theme-selection"]:checked',
  );
  if (selectedRadio) {
    state.selectedTheme = selectedRadio.value;
    localStorage.setItem("chatbot_selected_theme", state.selectedTheme);
    applyTheme();
    // Sync header dropdown
    if (elements.themeSelect) {
      elements.themeSelect.value = state.selectedTheme;
    }
    showToast("Theme updated");
  }
}

/**
 * Dynamically load a Google Font if not already loaded
 * @param {string} fontFamily - Font family CSS value (e.g., "'Cormorant Garamond', serif")
 */
function ensureFontLoaded(fontFamily) {
  // Map CSS font-family values to Google Fonts API names
  const fontMap = {
    "'Atkinson Hyperlegible', sans-serif": 'Atkinson+Hyperlegible:wght@400;700',
    "'IBM Plex Sans', sans-serif": 'IBM+Plex+Sans:wght@400;500;600',
    "'Crimson Pro', serif": 'Crimson+Pro:wght@400;500;600',
    "'Merriweather', serif": 'Merriweather:wght@400;700',
    "'Lora', serif": 'Lora:wght@400;500;600',
    "'IBM Plex Serif', serif": 'IBM+Plex+Serif:wght@400;500;600',
    "'Bitter', serif": 'Bitter:wght@400;500;600'
  };

  const googleFontName = fontMap[fontFamily];
  if (!googleFontName) {
    // Font is already loaded or not in our lazy-load list
    return;
  }

  // Check if font is already loaded
  const linkId = `font-${googleFontName.replace(/[^a-zA-Z0-9]/g, '-')}`;
  if (document.getElementById(linkId)) {
    return; // Already loaded
  }

  // Create and append font link
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${googleFontName}&display=swap`;
  document.head.appendChild(link);
}

/**
 * Initialize font customization from localStorage
 * Restores previously saved font type and size preferences
 */
export function initFontCustomization() {
  const savedFont = localStorage.getItem("edFontType");
  const savedSize = localStorage.getItem("edFontSize");

  if (savedFont) {
    elements.fontTypeSelect.value = savedFont;
    document.documentElement.style.setProperty(
      "--message-font-family",
      savedFont,
    );
    // Ensure the saved font is loaded
    ensureFontLoaded(savedFont);
  }

  if (savedSize) {
    elements.fontSizeSelect.value = savedSize;
    document.documentElement.style.setProperty(
      "--message-font-size",
      savedSize,
    );
  }

  // Initialize theme dropdown from state
  if (elements.themeSelect) {
    elements.themeSelect.value = state.selectedTheme;
  }
}

/**
 * Export ensureFontLoaded for use in event listeners
 */
export { ensureFontLoaded };

/**
 * Toggle between light and dark mode
 * Preserves the selected theme but switches mode
 */
export function toggleTheme() {
  state.theme = state.theme === "light" ? "dark" : "light";
  localStorage.setItem("chatbot_theme", state.theme);
  applyTheme();
}

/**
 * Update theme toggle button icon
 * Shows sun icon in dark mode, moon icon in light mode
 */
export function updateThemeIcon() {
  const icon = state.theme === "dark" ? icons.sun : icons.moon;
  elements.themeBtn.innerHTML =
    icon + '<span class="icon-label">Dark/Light</span>';
}
